import { Container, Header } from "semantic-ui-react";

export default function PrivacyView() {
  return (
    <Container style={{ maxWidth: 900, padding: "24px", color: "#222", fontFamily: 'Arial, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif', lineHeight: 1.6 }}>
      <Header as="h1" style={{ marginTop: "2rem", fontSize: "1.6rem", marginBottom: "0.2rem" }}>Privacy Policy</Header>
      <p style={{ fontSize: "0.9rem", color: "#555" }}>Effective date: <strong>01/01/2025</strong></p>
      <p>
        This Privacy Policy explains how Lunde Water LLC (“we”, “us”, or “our”) collects, uses, discloses, and protects personal information through our website{" "}
        <a href="https://lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>https://lundewaterllc.com</a> (the “Site”) and our services, including
        when you connect Intuit (e.g., QuickBooks) via our production developer integration.
      </p>

      <section>
        <h2 id="who-we-are" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>1. Who we are</h2>
        <p>Lunde Water LLC</p>
        <p>
          Website: <a href="https://lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>https://lundewaterllc.com</a>
        </p>
        <p>
          Contact: <a href="mailto:admin@lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>admin@lundewaterllc.com</a> (replace with your official contact)
        </p>
      </section>

      <section>
        <h2 id="scope" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>2. Scope and purpose</h2>
        <p>
          This Policy applies to personal data we collect from visitors, customers, and users when you interact with our Site, use our services, sign up for accounts,
          or connect third-party services such as Intuit via OAuth/API integrations. It describes the categories of personal information we collect, why we collect it,
          how we use it, with whom we share it, and your rights.
        </p>
      </section>

      <section>
        <h2 id="data-collected" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>3. Categories of personal information we collect</h2>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Contact & account data: name, email, phone, billing address, company name.</li>
          <li>Authentication & credentials: password hashes, OAuth tokens, API keys (securely stored).</li>
          <li>Financial & transaction data: invoices, payments, billing history (directly provided or accessed via Intuit/QuickBooks when authorized).</li>
          <li>Usage & technical data: IP address, device identifiers, browser type, pages visited, log files.</li>
          <li>Uploaded business documents & records: invoices, receipts, customer lists, and files you upload or permit us to retrieve.</li>
          <li>Support communications: emails, chat messages, and other correspondence.</li>
        </ul>
      </section>

      <section>
        <h2 id="how-we-collect" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>4. How we collect personal information</h2>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Directly from you (forms, account registration, uploads).</li>
          <li>Automatically via cookies, analytics, and server logs when you visit the Site.</li>
          <li>From third-party services you connect and authorize (for example, Intuit/QuickBooks via OAuth).</li>
          <li>From service providers that help us operate the Site and provide services (hosting, analytics, payment processors).</li>
        </ul>
      </section>

      <section>
        <h2 id="use" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>5. How we use personal information</h2>
        <p>We use personal information to:</p>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Provide, maintain, and improve our services and Site.</li>
          <li>Process transactions, billing, and customer support.</li>
          <li>Enable integrations and features you request (such as syncing invoices or customer data with Intuit).</li>
          <li>Communicate account- and service-related information.</li>
          <li>Detect and prevent fraud, abuse, and security incidents.</li>
          <li>Comply with legal obligations.</li>
        </ul>
      </section>

      <section>
        <h2 id="intuit-integration" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>6. Intuit (production developer) integration</h2>
        <div style={{ background: "#f6f9ff", borderLeft: "4px solid #2b6cb0", padding: "12px", margin: "12px 0" }}>
          <strong>Important:</strong> If you connect your Intuit account (e.g., QuickBooks Online) you will be presented with an Intuit consent screen describing the exact scopes and data we will access.
        </div>
        <p>
          When you authorize an Intuit connection, you permit Lunde Water LLC to access specified data from your Intuit account via Intuit’s API under the permissions you grant.
          Common uses include importing invoices, customers, account balances, and posting reconciliation or payment information back to Intuit where you request it.
        </p>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>
            Scope: We request only the minimum Intuit API scopes necessary for the features you enable. Replace the placeholder list below with your actual requested scopes:
            <ul>
              <li><code>com.intuit.quickbooks.accounting</code> — read and/or write (as required)</li>
              <li><code>openid</code>, <code>profile</code>, <code>email</code> — for user authentication</li>
            </ul>
          </li>
          <li>
            Tokens & storage: We store access tokens and refresh tokens securely; tokens are encrypted at rest. If you revoke access from Intuit or from your account settings with us, we will cease refreshing tokens.
          </li>
          <li>
            Roles: Intuit maintains its own privacy and security policies. We act as the data controller for the information we collect directly; for data accessed via Intuit, responsibilities are governed by Intuit’s developer terms and the consent you provide during the OAuth flow.
          </li>
          <li>
            No resale: We do not sell data obtained from Intuit. We will not use Intuit-provided data for marketing unrelated to your account without your explicit consent.
          </li>
        </ul>
      </section>

      <section>
        <h2 id="sharing" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>7. Sharing and disclosure</h2>
        <p>We may share personal information with:</p>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Service providers and subprocessors (hosting, payments, analytics, email providers) that perform services on our behalf.</li>
          <li>Intuit and other connected third-party platforms where you have authorized the connection.</li>
          <li>Legal authorities and regulators when required by law, or to protect rights, safety, or property.</li>
          <li>Third parties in connection with a business transaction such as a merger or sale (with notice to users where required).</li>
        </ul>
        <p>We require these parties to use personal information only as authorized and to implement reasonable security measures.</p>
      </section>

      <section>
        <h2 id="cookies" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>8. Cookies and tracking</h2>
        <p>We use cookies and similar technologies for functionality, analytics, and performance. You can control cookie preferences via your browser settings and any cookie banner we present. For analytics and advertising choices, use your browser or device-level privacy controls.</p>
      </section>

      <section>
        <h2 id="retention" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>9. Data retention</h2>
        <p>We retain personal information as long as needed to provide services, meet contractual obligations, comply with legal requirements, resolve disputes, and enforce agreements. If you close your account, we will delete or anonymize your personal information within a reasonable timeframe, unless retention is required by law. (Specify exact retention periods in your live policy.)</p>
      </section>

      <section>
        <h2 id="security" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>10. Security</h2>
        <p>We implement industry-standard technical and organizational measures to protect personal data, including encryption in transit (TLS) and at rest, access controls, and regular security assessments. No system can be guaranteed secure. If a data breach affecting your personal information occurs, we will notify you and regulators as required by law.</p>
      </section>

      <section>
        <h2 id="gdpr" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>11. GDPR (if you are in the EU/EEA or we process EU personal data)</h2>
        <p>If you are an individual in the European Economic Area (EEA) or the UK, the GDPR grants you certain rights and sets specific obligations for data controllers. This subsection explains how GDPR applies to our processing:</p>
        <h3>Lawful bases for processing</h3>
        <p>We rely on one or more lawful bases to process personal data, depending on the processing activity, including:</p>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Performance of a contract (to provide our services);</li>
          <li>Legal obligations (to comply with the law);</li>
          <li>Legitimate interests (e.g., fraud prevention, security, product improvement) where those interests are not overridden by your rights; and</li>
          <li>Your consent (where required), which you may withdraw at any time.</li>
        </ul>

        <h3>Your GDPR rights</h3>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Right to access: request a copy of personal data we hold about you.</li>
          <li>Right to rectification: correct inaccurate or incomplete data.</li>
          <li>Right to erasure: request deletion of your data (subject to legal exceptions).</li>
          <li>Right to restriction: request restriction of processing in certain circumstances.</li>
          <li>Right to data portability: receive your data in a structured, commonly used, machine-readable format.</li>
          <li>Right to object: object to processing based on legitimate interests or direct marketing.</li>
          <li>Right to withdraw consent: when processing relies on consent, you may withdraw it at any time.</li>
          <li>Right to lodge a complaint with a supervisory authority.</li>
        </ul>

        <p>To exercise GDPR rights, contact us at <a href="mailto:admin@lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>admin@lundewaterllc.com</a>. We will respond within the timeframes required by applicable law.</p>
      </section>

      <section>
        <h2 id="ccpa" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>12. CCPA/CPRA (if you are a California resident)</h2>
        <p>If you are a California resident, the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA) may grant you additional rights. This subsection summarizes those rights and how to exercise them:</p>

        <h3>Categories of personal information collected</h3>
        <p>We have collected the categories of personal information described in Section 3 for business and commercial purposes listed in this Policy.</p>

        <h3>Your CCPA/CPRA rights</h3>
        <ul style={{ margin: "0.5rem 0 0.8rem 1.2rem" }}>
          <li>Right to know: request disclosure of the categories and specific pieces of personal information we collected about you, the sources, business or commercial purpose for collection, and third parties with whom we shared it.</li>
          <li>Right to delete: request deletion of personal information we have collected about you, subject to exceptions.</li>
          <li>Right to opt-out of sale or sharing: opt-out if we sell or share personal information for cross-context behavioral advertising. (We do not sell personal information unless stated; if you want to opt-out, provide the method below.)</li>
          <li>Right to non-discrimination: you have the right not to receive discriminatory treatment for exercising these rights.</li>
        </ul>

        <h3>How to submit CCPA/CPRA requests</h3>
        <p>
          To exercise your CCPA/CPRA rights, contact us at <a href="mailto:admin@lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>admin@lundewaterllc.com</a>.
          You may also designate an authorized agent to make requests on your behalf (we may require proof of identity and authorization).
        </p>

        <p>
          <strong>Note:</strong> If you are a California resident and want to opt-out of the sale or sharing of personal information, please contact us at{" "}
          <a href="mailto:admin@lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>admin@lundewaterllc.com</a> or use the opt-out mechanism on our Site (if implemented).
        </p>
      </section>

      <section>
        <h2 id="children" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>13. Children’s privacy</h2>
        <p>Our Site and services are not intended for children under 13 (or higher minimum age under local law). We do not knowingly collect personal information from children. If we learn we have collected information from a child, we will delete it promptly.</p>
      </section>

      <section>
        <h2 id="international" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>14. International transfers</h2>
        <p>Personal data may be processed or stored in countries outside your country of residence. When data is transferred across borders, we apply appropriate safeguards (such as standard contractual clauses or other permitted mechanisms) to protect personal data, as required by law.</p>
      </section>

      <section>
        <h2 id="changes" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>15. Changes to this policy</h2>
        <p>We may update this Policy to reflect changes in our practices or legal requirements. We will publish the revised Policy on this page with a new “Effective date.” For material changes, we will provide additional notice as required by law.</p>
      </section>

      <section>
        <h2 id="contact" style={{ fontSize: "1.2rem", marginTop: "1.1rem", marginBottom: "0.2rem" }}>16. Contact us</h2>
        <p>To ask questions, exercise your rights, or submit privacy requests, contact:</p>
        <p>
          Email: <a href="mailto:admin@lundewaterllc.com" style={{ color: "#2b6cb0", textDecoration: "none" }}>admin@lundewaterllc.com</a> (replace with your official address)
        </p>
        <p>Address: 3370 Country Way, Acton, CA 93510</p>
      </section>

      <footer style={{ marginTop: "2rem", fontSize: "0.9rem", color: "#555" }}>
        Disclaimer: This policy is a template and does not constitute legal advice. Please review and adapt to your specific operations, any required retention periods, and applicable regulations. Consult legal counsel before publishing.
      </footer>
    </Container>
  );
}

